import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, Shield, Server, Database, Cookie } from "lucide-react";
import useSEO from "@/hooks/useSEO";

export default function Legal() {
  useSEO({
    title: "Mentions légales & confidentialité — IAM ROI Calculator Ariovis",
    description: "Éditeur Ariovis (SIREN 927 830 893), hébergement Emergent, CRM Odoo Belgique. Conformité RGPD, aucun cookie publicitaire, aucune donnée envoyée à une IA.",
    canonical: "https://iam-calculator.emergent.host/mentions-legales",
  });
  return (
    <div className="min-h-screen bg-slate-50">
      <Header minimal />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition mb-8" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>

        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">Mentions légales & confidentialité</div>
        <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight mb-10">
          Transparence sur l'éditeur,<br />
          <span className="ar-gradient-text">l'hébergement et vos données.</span>
        </h1>

        <div className="space-y-8">
          <Section title="Éditeur du site">
            <p className="text-slate-700 leading-relaxed">
              Le site IAM ROI Calculator est édité par <b>Ariovis</b>, société immatriculée au
              R.C.S. de Paris sous le numéro <b>927 830 893</b>, au capital de <b>5 000 €</b>,
              dont le siège social est situé au :
            </p>
            <p className="text-slate-900 font-medium mt-3">
              128 rue La Boétie<br />
              75008 Paris<br />
              France
            </p>
            <div className="mt-4 text-sm text-slate-600 space-y-1">
              <div><span className="text-slate-500">N° TVA intracommunautaire :</span> <b className="text-slate-900">FR91927830893</b></div>
              <div><span className="text-slate-500">Directeur de la publication :</span> <b className="text-slate-900">Matthieu Filizzola</b></div>
              <div><span className="text-slate-500">Téléphone :</span> <a href="tel:+33183644079" className="text-fuchsia-600 hover:underline">+33 1 83 64 40 79</a></div>
              <div><span className="text-slate-500">Email :</span> <a href="mailto:contact-siteinternet@ariovis.fr" className="text-fuchsia-600 hover:underline">contact-siteinternet@ariovis.fr</a></div>
            </div>
          </Section>

          <Section title="Hébergement" icon={Server}>
            <p className="text-slate-700 leading-relaxed">
              La présente application <b>IAM ROI Calculator</b> est hébergée sur l'infrastructure
              <b> Emergent</b> (plateforme conteneurisée managée).
            </p>
            <p className="text-slate-700 leading-relaxed mt-3">
              Pour le reste du site corporate Ariovis (ariovis.fr), l'hébergement est assuré par
              Webflow, 398 11th Street, Floor 2, San Francisco, CA 94103 — <a className="text-fuchsia-600 hover:underline" href="mailto:privacy@webflow.com">privacy@webflow.com</a>.
            </p>
          </Section>

          <Section title="Traitement des données personnelles" icon={Database}>
            <p className="text-slate-700 leading-relaxed">
              Lorsque vous demandez à recevoir votre étude PDF, vous nous transmettez les
              informations suivantes : <b>prénom, nom, email professionnel, société, secteur
              d'activité</b>. Ces informations sont :
            </p>
            <ul className="list-disc list-inside text-slate-700 leading-relaxed mt-3 space-y-2">
              <li>Utilisées <b>immédiatement</b> pour générer et vous envoyer votre étude PDF
                personnalisée (et notifier l'équipe Ariovis).</li>
              <li>Ensuite <b>stockées dans notre CRM Odoo, hébergé en Belgique (Union Européenne)</b>,
                à des <b>fins de prospection commerciale</b> par les équipes Ariovis (contact
                relatif à votre projet IAM, partage de contenus pertinents).</li>
              <li>Conservées tant que la relation commerciale est susceptible de se poursuivre,
                puis archivées conformément aux obligations légales.</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              Les <b>paramètres saisis</b> dans le calculateur (nombre d'utilisateurs, secteur,
              turnover, etc.) <b>ne sont pas conservés en base</b> par cette application : ils
              servent uniquement au calcul à la volée et apparaissent dans le PDF.
            </p>
            <p className="text-slate-700 leading-relaxed mt-3">
              <b>Aucune donnée n'est transmise à un service d'intelligence artificielle.</b>
              Le calcul est 100 % déterministe et exécuté sur nos serveurs.
            </p>
            <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">Vos droits (RGPD)</div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Conformément au Règlement (UE) 2016/679 (RGPD), vous disposez d'un droit d'accès,
                de rectification, d'effacement, de limitation, de portabilité et d'opposition sur
                vos données. Pour exercer ces droits, écrivez à
                <a className="text-fuchsia-600 hover:underline mx-1" href="mailto:contact-siteinternet@ariovis.fr">contact-siteinternet@ariovis.fr</a>
                ou à
                <a className="text-fuchsia-600 hover:underline ml-1" href="mailto:matthieu.filizzola@ariovis.fr">matthieu.filizzola@ariovis.fr</a>.
              </p>
            </div>
          </Section>

          <Section title="Cookies" icon={Cookie}>
            <p className="text-slate-700 leading-relaxed">
              Cette application <b>n'utilise aucun cookie publicitaire</b> ni cookie de
              tracking marketing. Seuls les cookies strictement nécessaires au fonctionnement
              technique de la session sont éventuellement déposés. Aucun consentement
              publicitaire ne vous est demandé car aucun tracking publicitaire n'est mis en œuvre.
            </p>
          </Section>

          <Section title="Propriété intellectuelle" icon={Shield}>
            <p className="text-slate-700 leading-relaxed">
              Les marques, logos et contenus présents sur ce site sont la propriété
              d'Ariovis. Toute reproduction, totale ou partielle, sans autorisation préalable
              écrite d'Ariovis est interdite (articles L713-2 et L713-3 du Code de la propriété
              intellectuelle).
            </p>
            <p className="text-slate-700 leading-relaxed mt-3">
              Les méthodologies et hypothèses chargées dans le moteur de calcul s'appuient sur
              le savoir-faire propriétaire d'Ariovis ainsi que sur des sources publiques agnostiques
              (NIST, Verizon DBIR, IBM/Ponemon, Gartner, Flexera, ITAM Forum).
            </p>
          </Section>

          <Section title="Droit applicable">
            <p className="text-slate-700 leading-relaxed">
              Le présent site et son utilisation sont soumis au <b>droit français</b>.
              Tout litige relatif à son utilisation relèvera de la compétence exclusive des
              tribunaux du ressort de la Cour d'Appel de Paris.
            </p>
          </Section>

          <p className="text-xs text-slate-500 pt-4">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-3xl p-7 lg:p-8">
      <div className="flex items-center gap-3 mb-4">
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
        )}
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
      </div>
      <div className="text-sm">{children}</div>
    </section>
  );
}
